/**
 * viewerHealth — client-side probes for a customer's publicUrl / file URL
 * to detect whether the bucket is publicly reachable AND CORS allows
 * cross-origin reads from Box and GenesisView.
 *
 * Used by the connection card status row ("CORS: ✓ / ✗") and by the
 * Setup Guide Verify tab. 8s timeout.
 *
 * Probes the public file host root (`{publicUrl}/`). If the root returns
 * 2xx we know the host serves cross-origin content. If browser fetch throws
 * we infer CORS blocking. If we get any HTTP response (incl. 403/404)
 * the host is reachable. A denied directory listing is fine because the
 * viewer fetches specific file keys.
 *
 * Three result states:
 *   ok           - reachable AND CORS-permitted (any 2xx/3xx/4xx response)
 *   cors-blocked - browser blocked the response (fetch threw)
 *   unreachable  - DNS/network failure
 *
 * Note: heuristic — the browser hides CORS error details from JS, so
 * we infer cors-blocked from a NetworkError-style throw. Some false
 * positives expected; this is informational, not authoritative.
 */

import {
  getMissingViewCorsCoreHeaders,
  getVisibleViewCorsHeaders,
  hasReadablePdfRangeHeaders,
} from './viewCorsProtocol';

export type ViewerHealth = 'ok' | 'cors-blocked' | 'unreachable' | 'unchecked';

export type ViewCorsRangeStatus =
  | 'ok'
  | 'headers-hidden'
  | 'not-supported'
  | 'blocked'
  | 'unchecked';

export type ViewCorsUrlProbe =
  | { kind: 'unchecked' }
  | { kind: 'invalid-url'; message: string }
  | { kind: 'timeout' }
  | { kind: 'cors-blocked'; message: string }
  | { kind: 'unreachable'; message: string }
  | {
      kind: 'success';
      status: number;
      rangeStatus: ViewCorsRangeStatus;
      visibleHeaders: string[];
      missingCoreHeaders: string[];
    };

const HEALTH_TIMEOUT_MS = 8000;

/**
 * Probe a customer's publicUrl root and classify the result.
 *
 * @param publicUrl - normalized https URL (no trailing slash) of the
 *   customer's public file base URL
 * @returns one of the ViewerHealth states
 */
export async function probeViewerHealth(publicUrl: string): Promise<ViewerHealth> {
  if (!publicUrl) return 'unchecked';
  const trimmed = publicUrl.replace(/\/+$/, '');
  if (!/^https:\/\//i.test(trimmed)) return 'unreachable';

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    // Probe the public host root. R2 may return 200 (index page enabled),
    // 403 (listing forbidden), or 404 (no index) — all of which mean
    // "reachable + CORS-permitted". A network/CORS error throws.
    const res = await fetch(trimmed + '/', {
      method: 'HEAD',
      mode: 'cors',
      signal: controller.signal,
      credentials: 'omit',
    });
    // Any HTTP response means the network + CORS layer worked.
    return res.status > 0 ? 'ok' : 'unreachable';
  } catch (err) {
    const msg = (err as Error)?.message || String(err);
    if (/fetch|network|cors/i.test(msg)) return 'cors-blocked';
    return 'unreachable';
  } finally {
    clearTimeout(timer);
  }
}

function normalizeHttpsUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || !/^https:\/\//i.test(trimmed)) return null;
  try {
    return new URL(trimmed).toString();
  } catch {
    return null;
  }
}

function classifyFetchError(err: unknown): ViewCorsUrlProbe {
  if ((err as Error)?.name === 'AbortError') return { kind: 'timeout' };
  const message = (err as Error)?.message || String(err);
  if (/fetch|network|cors/i.test(message)) return { kind: 'cors-blocked', message };
  return { kind: 'unreachable', message };
}

async function probeRangeHeaders(url: string): Promise<{
  rangeStatus: ViewCorsRangeStatus;
  visibleHeaders: string[];
  missingCoreHeaders: string[];
}> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: { Range: 'bytes=0-0' },
      signal: controller.signal,
    });
    const visibleHeaders = getVisibleViewCorsHeaders(res.headers);
    const missingCoreHeaders = getMissingViewCorsCoreHeaders(res.headers);
    const rangeStatus: ViewCorsRangeStatus = res.status === 206
      ? (hasReadablePdfRangeHeaders(res.headers) ? 'ok' : 'headers-hidden')
      : (res.status === 200 ? 'not-supported' : 'headers-hidden');
    return { rangeStatus, visibleHeaders, missingCoreHeaders };
  } catch {
    return { rangeStatus: 'blocked', visibleHeaders: [], missingCoreHeaders: [] };
  } finally {
    controller.abort();
    clearTimeout(timer);
  }
}

export async function probeViewCorsUrl(fileUrl: string): Promise<ViewCorsUrlProbe> {
  const url = normalizeHttpsUrl(fileUrl);
  if (!url) {
    return { kind: 'invalid-url', message: 'Enter a full https:// file URL.' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    });
    const range = await probeRangeHeaders(url);
    const headVisibleHeaders = getVisibleViewCorsHeaders(res.headers);
    const visibleHeaders = Array.from(new Set([...headVisibleHeaders, ...range.visibleHeaders]));
    const missingCoreHeaders = range.rangeStatus === 'blocked'
      ? getMissingViewCorsCoreHeaders(res.headers)
      : range.missingCoreHeaders;
    return {
      kind: 'success',
      status: res.status,
      rangeStatus: range.rangeStatus,
      visibleHeaders,
      missingCoreHeaders,
    };
  } catch (err) {
    return classifyFetchError(err);
  } finally {
    clearTimeout(timer);
  }
}
